import React, { useEffect, useState, useSyncExternalStore } from "react";
import { HighlightedDrivers } from "./driverColours";
import { supabaseAnon } from "../db";
import { useNavigate } from "react-router-dom";

// Function to add the years from 1950 to the current year to be mapped as options
const addYears = () => {
  const yearArray = [];
  const currentYear = new Date().getFullYear();

  for (let year = currentYear; year >= 2018; year--) {
    yearArray.push(year);
  }

  return yearArray;
};

// Function to dynamically represent the button text dependent on the page the user is on
const getButtonText = (page) => {
  if (page === "gear-figure") {
    return "Gear up!";
  } else if (page === "lap-time-comparison") {
    return "Compare Laps Now";
  } else if (page === "ai-coach") {
    return "Load Session";
  }
};

// Main component
const Dropdowns = ({
  isPremium,
  setPage,
  page,
  setDropdownStates,
  figureImage,
  setFigureImage,
  isLoadingResult = false,
  raceResultLoaded,
  setRaceResultLoaded,
}) => {
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [trackID, setTrackID] = useState([]);
  const [driverNames, setDriverNames] = useState([]);
  const [driverNumbers, setDriverNumbers] = useState([]);
  const [session, setSession] = useState("");
  const [yearSelected, setYearSelected] = useState(new Date().getFullYear());
  const [driverSelected, setDriverSelected] = useState("");
  const [driversSelected, setDriversSelected] = useState([]);
  const [driversNumberSelected, setDriverNumberSelected] = useState("");
  const [displaySuccessResponse, setDisplaySuccessResponse] = useState(false);
  const [displaySuccessRequest, setDisplaySuccessRequest] = useState(false);
  const [displayFailedRequest, setDisplayFailedRequest] = useState(false);
  const [displayFailedResponse, setDisplayFailedResponse] = useState(false);
  useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showUpgradePromptText, setShowUpgradePromptText] = useState("");

  const navigate = useNavigate();

  const years = addYears();
  const sessions = ["Practice", "Qualifying", "Race"];

  // UseEffect to get all the tracks for the year and set the state
  useEffect(() => {
    // Get all tracks
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/telemetry/track?year=${yearSelected}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
      .then((res) => res.json())
      .then(setTracks)
      .catch((error) => {
        console.log(error);
      });

    setTrackID("");
    setDriverNames([]);
    setDriverNumbers([]);
  }, [yearSelected]);

  // UseEffect to set the drivers for the track
  useEffect(() => {
    // Get all drivers
    if (trackID.length > 0) {
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/telemetry/drivers?year=${yearSelected}&trackID=${trackID}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
        .then((res) => res.json())
        .then((response) => {
          setDriverNames(response.map((driver) => driver.name));
          setDriverNumbers(response.map((driver) => driver.number));
        })
        .catch((error) => {
          console.error(error);
          setDriverNames([]);
          setDriverNumbers([]);
        });
    }
  }, [yearSelected, trackID]);

  useEffect(() => {
    if (displaySuccessResponse) {
      const timer = setTimeout(() => {
        setDisplaySuccessResponse(false);
      }, 3000);

      return () => clearTimeout(timer);
    }

    if (displaySuccessRequest) {
      const timer = setTimeout(() => {
        setDisplaySuccessRequest(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [displaySuccessResponse, displaySuccessRequest]);

  useEffect(() => {
    if (displayFailedRequest) {
      const timer = setTimeout(() => {
        setDisplayFailedRequest(false);
      }, 3000);

      return () => clearTimeout(timer);
    }

    if (displayFailedResponse) {
      const timer = setTimeout(() => {
        setDisplayFailedResponse(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [displayFailedRequest, displayFailedResponse]);

  useEffect(() => {
    if (HighlightedDrivers) {
      while (HighlightedDrivers.length > 0) {
        HighlightedDrivers.pop();
      }
    }

    setFigureImage();
  }, [page]);

  useEffect(() => {
    setDriverNumberSelected(getDriverNumberIndex());
  }, [driversSelected]);

  // Free plan can only analyse as far back as 2024.
  const doesYearSelectedMatchPlan = (yearRequested) => {
    const yearsAllowed = ["2024", "2025", "2026"]; // Limited for free users

    if (!isPremium) {
      if (yearsAllowed.includes(yearRequested)) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (!isPremium && driversSelected.length > 1) {
      const updatedDriversSelected = driversSelected.pop();
      setDriversSelected([]);
      setDriversSelected([updatedDriversSelected]);
      setShowUpgradePromptText(
        "Multiple driver selection is a premium feature. Upgrade to Premium to access this feature."
      );
      setShowUpgradePrompt(true);
    }
  }, [driversSelected]);

  useEffect(() => {
    setShowUpgradePrompt(false);
  }, [yearSelected, trackID, session, page]);

  // Function to change the year that is selected to 2024 if a free user tries to select a year they cannot access
  const changeYearSelected = (yearRequested) => {
    yearRequested = 2024;
    return yearRequested;
  };

  // Dynamically adding and removing drivers dependent on what input the user gives (compare drivers page)
  const changeHighlightedDrivers = (target) => {
    if (HighlightedDrivers.length > 0) {
      if (!HighlightedDrivers.includes(target)) {
        HighlightedDrivers.push(target);
      } else {
        const indexToRemove = HighlightedDrivers.indexOf(target);
        HighlightedDrivers.splice(indexToRemove, 1);
      }
    } else {
      HighlightedDrivers.push(target);
    }

    return HighlightedDrivers;
  };

  const getDriverNumberIndex = () => {
    const numbers = [];
    // If only a singular driver has been selected
    if (driversSelected) {
      for (let driver of driversSelected) {
        const driverNumberIndex = driverNames.indexOf(driver);
        const driverNumber = driverNumbers[driverNumberIndex];
        numbers.push(driverNumber);
      }

      console.log(numbers);

      return numbers;
    }
  };

  const makeCheckParametersRequest = async () => {
    try {
      const checkParmatersResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/figures/check-parameters`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            figureName: page,
            year: yearSelected,
            circuit: trackID,
            session: session,
            drivers: driversSelected,
          }),
        }
      );

      console.log(checkParmatersResponse.status);
      if (!checkParmatersResponse.ok || checkParmatersResponse.status !== 200) {
        setDisplayFailedRequest(true);
        return;
      }

      const data = checkParmatersResponse.json();
      return data;
    } catch (error) {
      console.error(error);
    }
  };

  const makeFigureRequest = async () => {
    try {
      const {
        data: { user },
      } = await supabaseAnon.auth.getUser();

      console.log(yearSelected, trackID);

      // Get race round
      const raceRoundResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/telemetry/round?year=${yearSelected}&trackID=${trackID}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const raceRound = await raceRoundResponse.json();

      console.log(raceRound);

      const figureResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/figures/figure-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userID: JSON.parse(localStorage.getItem("sb_id")).id,
            figureName: page,
            year: yearSelected,
            circuit: raceRound,
            session: session,
            drivers: driversSelected,
            driverNumbers: driversNumberSelected,
            circuitName: trackID,
          }),
        }
      );

      if (figureResponse.ok) {
        if (figureResponse.status === 200) {
          setDisplaySuccessResponse(true);
          const data = await figureResponse.bytes();
          return data;
        } else {
          setDisplayFailedResponse(true);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // API call to create the figure for a specific page
  const createFigureRequest = async (isDriverRequired) => {
    // Prevent duplicate submissions
    if (isProcessing) return;

    const inputsValid =
      yearSelected &&
      trackID &&
      session &&
      (isDriverRequired ? driversSelected.length > 0 : true);

    console.log(inputsValid);

    if (!inputsValid) {
      setDisplayFailedRequest(true);
      return;
    }

    setIsProcessing(true);
    try {
      // Make request to the backend for this figure
      const paramatersResponse = await makeCheckParametersRequest();
      if (!paramatersResponse || !paramatersResponse.message) {
        setDisplayFailedRequest(true);
        return;
      }

      const figureID = paramatersResponse.message.figureID;

      if (typeof figureID === "number" && !displayFailedRequest) {
        const figureRequest = await makeFigureRequest();

        if (figureRequest) {
          const file = figureRequest;
          if (file) {
            const blob = new Blob([file], { type: "image/png" });
            const imageURL = URL.createObjectURL(blob);
            setFigureImage(imageURL);
          }
        } else {
          console.error(figureRequest);
          setDisplayFailedResponse(true);
        }
      } else {
        setDisplayFailedRequest(true);
      }
    } catch (err) {
      console.error("createFigureRequest error", err);
      setDisplayFailedRequest(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to retrun the current values of the dropdown for AI Coach page - user still has to choose an analysis type
  const returnDropdownValues = () => {
    if (yearSelected && trackID) {
      setDropdownStates([yearSelected, trackID]);
      setRaceResultLoaded(false);
    } else {
      setDropdownStates([]);
    }
  };

  // Function to handle the logic for when the user presses the 'load session' button
  const handleDropdownSubmit = () => {
    if (page === "ai-coach") {
      console.log(raceResultLoaded);
      returnDropdownValues();
      raceResultLoaded == false
        ? setIsProcessing(true)
        : setIsProcessing(false);
    } else if (page === "lap-time-comparison") {
      createFigureRequest(false);
    } else {
      createFigureRequest(true);
    }
  };

  // Dynamically changing what functionality the driver dropdown should have the compare drivers page allows mutliple drivers to be selected.
  // Dependent on the page a different state is assigned.
  const getDriversDropdown = () => {
    if (page === "ai-coach" || page === "lap-time-comparison") {
      return (
        <li>
          {page !== "ai-coach" ||
            (page !== "lap-time-comparison" && (
              <select
                name="driver"
                id="driver"
                disabled={!session.length || isProcessing || isLoadingResult}
                onInput={(e) => {
                  setDriverSelected(e.target.value);
                }}
              >
                <option value="">Select driver...</option>
                {(driverNames || []).map((driver) => (
                  <option key={driver} value={driver}>
                    {HighlightedDrivers.length > 0
                      ? driversSelected[driversSelected.length - 1]
                      : driver}
                  </option>
                ))}
              </select>
            ))}
        </li>
      );
    } else {
      return (
        <li>
          <select
            name="driver"
            id="driver"
            disabled={!session.length || isProcessing || isLoadingResult}
            onChange={(e) => {
              setDriversSelected(
                changeHighlightedDrivers(e.target.value).map((driver) => driver)
              );
            }}
          >
            <option value="">Select drivers...</option>
            {(driverNames || []).map((driver) => (
              <option
                key={driver}
                value={driver}
                className={
                  driversSelected.includes(driver)
                    ? "selected-driver"
                    : "driver"
                }
              >
                {driver}
              </option>
            ))}
          </select>
        </li>
      );
    }
  };

  // Function to map the highlighted drivers component so they can be displayed below
  const createHighlightedDriversList = () => {
    return (
      <ul className="highlighted-drivers-list">
        {(driversSelected || []).map((driver) => (
          <li key={driver}>
            {driver}
            <button
              value={driver}
              title="Remove Driver"
              onClick={(e) =>
                setDriversSelected(
                  changeHighlightedDrivers(e.target.value).map(
                    (driver) => driver
                  )
                )
              }
            >
              X
            </button>
          </li>
        ))}
      </ul>
    );
  };

  // Main JSX
  return (
    <div className="dropdowns-container">
      {displaySuccessRequest && (
        <div className="success-message">Request Sent!</div>
      )}

      {displaySuccessResponse && (
        <div className="success-message">Figure Loaded!</div>
      )}

      {displayFailedRequest && (
        <div className="failed-message">Request failed to send!</div>
      )}

      {displayFailedResponse && (
        <div className="failed-message">Figure failed to load!</div>
      )}

      {showUpgradePrompt && (
        <div className="upgrade-prompt">
          <div className="upgrade-prompt-inner">
            <p>
              {showUpgradePromptText ||
                "The selected option is a premium feature. Please upgrade to TelemetryLab Premium to access this feature."}
            </p>
            <div className="upgrade-prompt-actions">
              <button
                onClick={() => {
                  setShowUpgradePrompt(false);
                  navigate("/payment");
                  if (typeof setPage === "function") setPage("payment");
                }}
              >
                Upgrade
              </button>
              <button
                onClick={() => {
                  setShowUpgradePrompt(false);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ul className="dropdowns">
        <li>
          <select
            className={isPremium ? "premium-year" : "free-year"}
            name="year"
            id="year"
            value={yearSelected}
            disabled={isProcessing || isLoadingResult}
            onChange={(e) => {
              const selected = e.target.value;
              if (!doesYearSelectedMatchPlan(selected)) {
                // reset to allowed year and show upgrade prompt
                setYearSelected(changeYearSelected(selected));
                setShowUpgradePromptText(
                  "Years before 2024 are exclusive to premium. Upgrade to TelemetryLab Premium to access all years."
                );
                setShowUpgradePrompt(true);
              } else {
                setYearSelected(selected);
              }
            }}
          >
            <option value="select-year">Select Year...</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </li>

        <li>
          <select
            name="tracks"
            id="tracks"
            disabled={!tracks.length || isProcessing || isLoadingResult}
            onChange={(e) => {
              setTrackID(e.target.value);
              setDriversSelected([]);
            }}
          >
            <option value="">Select circuit...</option>
            {tracks.map((track) => (
              <option key={track.id} value={track.id}>
                {track.name}
              </option>
            ))}
          </select>
        </li>

        <li>
          {page !== "ai-coach" && (
            <select
              className={page !== "ai-coach" ? "" : "unrequired"}
              name="session"
              id="session"
              disabled={!tracks.length || isProcessing || isLoadingResult}
              onChange={(e) => setSession(e.target.value)}
            >
              <option value="">Select a session...</option>
              {sessions.map((session) => (
                <option key={session} value={session}>
                  {session}
                </option>
              ))}
            </select>
          )}
        </li>

        {getDriversDropdown()}
      </ul>

      <div className="highlighted-drivers">
        {page === "gear-figure" || page === "lap-time-comparison"
          ? createHighlightedDriversList()
          : ""}
      </div>

      <div className="dropdown-button">
        <button
          onClick={() => {
            handleDropdownSubmit();
          }}
          type="submit"
          disabled={isProcessing || isLoadingResult}
        >
          {isProcessing || isLoadingResult ? "Loading..." : getButtonText(page)}
        </button>
      </div>
    </div>
  );
};

export default Dropdowns;
